import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { StickyWithHeaderObserver } from '@thd-olt-component-react/sticky';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { MajorApplianceFulfillment } from '@thd-olt-component-react/fulfillment';
import { ProductAddOns } from '@thd-olt-component-react/product-add-ons';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';

import { PaypalPriceMessaging } from '@thd-olt-component-react/paypal-checkout-button';
import classNames from 'classnames';
import { alias, client, extend, params, useDataModel } from '@thd-nucleus/data-sources';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  number as numberType,
  shape as shapeType,
  string as stringType
} from '@thd-nucleus/data-sources/react/dataModel/prop-types';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { BuyboxAddToProject } from './subcomponents/BuyboxAddToProject';
import { BuyboxAddToQuote } from './subcomponents/BuyboxAddToQuote';
import { BuyboxError } from './subcomponents/BuyboxError';
import { BuyboxLoader } from './subcomponents/BuyboxLoader';
import * as helper from '../BuyboxHelper';
import {
  ATC_TEXT,
  BOGO_ATC_TEXT,
  FULFILLMENT_METHOD_APPLIANCE,
  FULFILLMENT_METHOD_BOPIS
} from '../constants';
import { isMobile } from '../util';
import { useBogoInfo } from './hooks/useBogoInfo';
import { PartsAndServicesDropDownContainer } from '@thd-olt-component-react/parts-and-services';
import { BuyboxAddToList } from './subcomponents/BuyboxAddToList';
import useConfiguratorEvents from './hooks/useConfiguratorEvents';

const { bool, shape, string } = proptypes;

const getItemIdFromConfigurator = (items) => {
  if (items) {
    return items.find((item) => item.isAnchor).itemId.toString();
  }
  return '';
};

function renderEarlyStates({ error, data, loading, isServer }) {
  if (error && !data) {
    return <BuyboxError />;
  }
  if (loading || isServer) {
    return <BuyboxLoader tilesCount={2} />;
  }
  return null;
}

function isProductDiscontinued(availabilityData) {
  return availabilityData?.checkAvailability?.fulfillmentOptions?.some(
    (option) => option?.services?.some(
      (service) => service?.deliveryAvailabilityDetail?.some(
        (detail) => detail?.assortmentStrategy === 'Inactive Discontinue'
      )
    )
  );
}

/**
 * Buybox container
 */
const MajorApplianceBuyBox = (props = {}) => {
  const {
    stash = {},
    showCards = false,
    sticky = false,
    showFavorites = false,
    showAddToProject = false,
    noPipSpacing = false,
  } = props;
  const {
    channel,
    deliveryZip,
    isServer,
    isConsumerApp
  } = useContext(ExperienceContext);

  const buyboxActionsRef = useRef(null);

  const bogoInfo = useBogoInfo();

  let { itemId } = props;

  const covidAlert = useConfigService('fs-prop:buy-area__buybox--covidAlert');
  const bopisMinimumThreshold = useConfigService('fs-prop:buy-area__buybox--bopisMinimumThreshold');
  const bopisMinimumThresholdStores = useConfigService('fs-prop:buy-area__buybox--bopisMinimumThresholdStores');
  const isTwoTileEnabledGlobal = useConfigService('isTwoTileEnabled');
  const enableAddToProjectFS = useConfigService('fs:enableAddToProjectBuyBox');
  const enableMAAddToQuoteFS = useConfigService('fs:enableMAAddToQuoteBuyBox') || false;
  const enableAssortmentStrategyDiscontinued = useConfigService('enableAssortmentStrategyDiscontinued') || false;
  const enableVirtualAssociatesBuiltIns = useConfigService('enableVirtualAssociatesBuiltIns') || false;
  const virtualAssistantCategories = useConfigService('virtualAssistantCategories') || '';
  const pipPnSWidgetEnabled = useConfigService('fs:pipPnSWidgetEnabled') || false;
  const isAddToProjectsEnabled = enableAddToProjectFS && showAddToProject;

  const theme = useTheme(MajorApplianceBuyBox);
  const { hidePaypal, hideAddToQuoteButton } = theme.props;

  const isTwoTileEnabledFS = isTwoTileEnabledGlobal;
  const { storeId, storeZip } = useStore();
  const { configuratorUnavailable } = useConfiguratorEvents();
  const state = {
    fulfillmentMethod: FULFILLMENT_METHOD_APPLIANCE,
    isShowATC: false,
    zipCode: '',
    disableATC: false,
    quantity: 1
  };

  const [buyboxState, setBuyboxState] = useState(state);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const { items } = useLifeCycleEventBus('configurator.configurator_available_items');
  let configurableApplianceItemIds;
  if (items) {
    itemId = getItemIdFromConfigurator(items) || itemId;
    configurableApplianceItemIds = items.map((item) => item.itemId.toString());
  }
  const { configuratorData } = useLifeCycleEventBus('configurator.configurator_data');
  const configuratorCart = configuratorData?.product?.configuration?.cart;
  const atcStash = helper.useATCLocationDDOStash({ stash, itemId });

  const productOptions = {
    ssr: false,
    variables: {
      itemId,
      storeId,
      zipCode: storeZip
    }
  };

  const { data, loading, error } = useDataModel('clientOnlyProduct', productOptions);

  const {
    fulfillment = {},
    pricing = {},
    info = {}
  } = data?.product || {};

  const classConfig = info?.productDepartment + '-' + info?.classNumber + '-' + info?.subClassNumber;
  const isVirtualAssociatesBuiltIns = enableVirtualAssociatesBuiltIns && (`,${virtualAssistantCategories},`.includes(`,${classConfig},`));

  const { data: availabilityData } = useDataModel('checkAvailability', {
    variables: {
      itemIds: [itemId],
      zipCode: deliveryZip,
      localStoreId: storeId
    },
    ssr: false,
    skip: !enableAssortmentStrategyDiscontinued || !itemId || !deliveryZip || !storeId
  });

  let isDiscontinued = false;

  if (enableAssortmentStrategyDiscontinued) {
    isDiscontinued = isProductDiscontinued(availabilityData);
  }

  const isPaypalEnabled = useConfigService('PAYPAL_ENABLE');
  const isPaypalPayInFour = useConfigService('paypalPayInFour');
  const isPromoPIP = useMemo(() => {
    if (pricing?.conditionalPromotions) {
      return pricing?.conditionalPromotions.find((promotion) => {
        return (promotion.experienceTag === 'BMSM'
          || promotion.experienceTag === 'MSBONLINE'
          || promotion.experienceTag === 'BOGO');
      });
    }
    return false;
  }, [pricing?.conditionalPromotions]);
  const isMAPPrice = pricing?.message === 'see final price in cart';

  const showPaypalMessaging = !hidePaypal && isPaypalEnabled && isPaypalPayInFour;
  const isGenericMessaging = isMAPPrice || isPromoPIP;

  const onFulfillmentChange = ({
    method,
    isShowATC,
    zipCode,
    disableATC,
    primaryStoreNum
  }) => {
    setBuyboxState((prevState) => {

      return {
        ...prevState,
        fulfillmentMethod: method,
        isShowATC,
        zipCode,
        disableATC,
        primaryStoreNum
      };
    });
  };

  // call back on add to cart click
  const onATCClick = useCallback(() => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('@@ADD-TO-CART/LOCATION', atcStash);
      if (pipPnSWidgetEnabled) {
        window.LIFE_CYCLE_EVENT_BUS.trigger('parts-and-services-drawer.atc-hdpp-widget-test', selectedAddOns?.[itemId] || {});
      }
    }
  }, [atcStash, selectedAddOns, itemId]);

  const {
    isShowATC,
    quantity,
    zipCode,
    fulfillmentMethod,
    disableATC,
    primaryStoreNum
  } = buyboxState;

  const hasBopisService = fulfillment?.fulfillmentOptions?.some(
    (option) => option.type === 'pickup' && option.services?.some(
      (service) => service.type === 'bopis'
    )
  );
  const isBopisAndPickup = fulfillmentMethod === FULFILLMENT_METHOD_BOPIS && !!hasBopisService;

  const earlyState = renderEarlyStates({ error, data, loading, isServer });
  if (earlyState) {
    return earlyState;
  }

  const buttonText = bogoInfo?.bogoSelectedProduct?.itemId ? BOGO_ATC_TEXT : ATC_TEXT;

  const cartReqParams = configuratorCart
    || helper.getCartReqParams({
      itemId,
      bogoInfo,
      fulfillmentMethod,
      configurableItems: items,
      quantity,
      zipCode,
      storeId,
      fulfillment,
      primaryStoreNum,
      selectedAddOns
    });

  const cartOptions = helper.getCartOptions({
    channel,
    configurableItems: items,
    bogoInfo,
    fulfillmentMethod,
    alterBrowserHistory: true,
    zipCode,
    storeId
  });
  if (!data) {
    return null;
  }

  const stickyClassnames = classNames('sui-bg-primary sui-left-0 sui-flex sui-flex-col sui-w-full sui-p-4  sui-gap-4', {
    'buybox__actions--sticky sui-d-none': !isConsumerApp,
  });

  const addToCartNode = (
    <AddToCart
      cartOptions={cartOptions}
      cartReqParams={cartReqParams}
      onClick={onATCClick}
      disabled={configuratorUnavailable || disableATC}
      showIcon
    >
      {buttonText}
    </AddToCart>
  );

  const addToListNode = (
    <div className={!isAddToProjectsEnabled ? 'sui-col-span-2' : ''}>
      <BuyboxAddToList quantity={quantity} itemId={itemId} />
    </div>
  );

  const showAddToQuote = enableMAAddToQuoteFS && !hideAddToQuoteButton
    && fulfillmentMethod === FULFILLMENT_METHOD_APPLIANCE;

  const addToProjectsNode = (
    <div className={(!showFavorites || showAddToQuote) ? 'sui-col-span-2' : ''}>
      <BuyboxAddToProject
        itemId={itemId}
        quantity={quantity}
      />
    </div>
  );

  const addToQuoteNode = (
    <BuyboxAddToQuote
      itemId={itemId}
      fulfillmentLocation={
        helper.getFulfillmentLocation(fulfillment, fulfillmentMethod, storeZip, storeId, zipCode)
      }
      fulfillmentMethod={fulfillmentMethod}
      quantity={quantity}
      disabled={configuratorUnavailable || disableATC}
      applianceDeliveryStore={primaryStoreNum}
      enableApplianceAddToQuote={enableMAAddToQuoteFS}
    />
  );

  const showATC = isShowATC && !isDiscontinued;

  const fulfillmentContainerClasses = classNames('sui-bg-primary sui-w-full sui-mb-4', {
    'sui-p-4': !noPipSpacing
  });

  const productAddOnsContainerClasses = classNames('sui-bg-primary sui-w-full sui-mb-4', {
    'sui-p-4': !noPipSpacing
  });

  const atcContainerClasses = classNames('sui-bg-primary sui-w-full', {
    'sui-p-4': !noPipSpacing
  });

  const wrapperClasses = classNames({
    'sui-bg-subtle': !noPipSpacing,
    'sui-bg-primary': noPipSpacing
  });

  return (
    <div
      className={'sui-grow sui-overflow-hidden sui-w-full ' + (isMobile(channel) && 'sui-mb-4')}
      data-section="MajorApplianceBuyBox"
      data-component="MajorApplianceBuyBox"
    >
      {sticky && isMobile(channel) && (
        <StickyWithHeaderObserver
          boxShadow
          className={stickyClassnames}
          childRef={buyboxActionsRef}
          forwardRef={buyboxActionsRef}
          offsetScroll={18}
          position="bottom"
          stickyByDefault
          stickyType="forwardRef"
          visibleByDefault
        >
          {showATC && (
            <div className="sui-flex sui-flex-col sui-gap-4">
              <div className="sui-flex sui-flex-col">
                {addToCartNode}
              </div>
              {(showFavorites || isAddToProjectsEnabled || showAddToQuote) && (
                <div className="sui-grid sui-grid-cols-2 sui-gap-4">
                  {showFavorites && addToListNode}
                  {showAddToQuote && addToQuoteNode}
                  {isAddToProjectsEnabled && addToProjectsNode}
                </div>
              )}
            </div>
          )}
        </StickyWithHeaderObserver>
      )}
      {pipPnSWidgetEnabled && (
        <PartsAndServicesDropDownContainer
          itemId={itemId}
          lineItemId={itemId}
          deliveryZip={deliveryZip || storeZip}
          selectedAddOns={selectedAddOns}
          setSelectedAddOns={setSelectedAddOns}
          productInfo={info}
          isBopisAndPickup={isBopisAndPickup}
        />
      )}
      <div className={wrapperClasses}>
        <div className={fulfillmentContainerClasses}>
          <MajorApplianceFulfillment
            itemId={itemId}
            configurableApplianceItemIds={configurableApplianceItemIds}
            covidAlert={covidAlert}
            quantity={quantity}
            onChange={onFulfillmentChange}
            bopisMinimumThreshold={bopisMinimumThreshold}
            bopisMinimumThresholdStores={bopisMinimumThresholdStores}
            isTwoTileEnabledFS={isTwoTileEnabledFS}
          />
        </div>
        {isVirtualAssociatesBuiltIns && (
          <div className={productAddOnsContainerClasses}>
            <ProductAddOns
              itemId={itemId}
              onChange={() => { }}
              hideProReferral
              showCards={false}
              hideProtectionPlan
              availableFulfillments={['Appliance']}
              isTwoTileEnabled
              isShowATC
            />
          </div>
        )}
        {showATC && (
          <div
            className={atcContainerClasses}
            ref={buyboxActionsRef}
          >
            <div className="sui-flex sui-flex-col sui-w-full sm:sui-w-96 sui-gap-4">
              <div className="sui-flex sui-flex-col">
                {addToCartNode}
              </div>
              {(showFavorites || isAddToProjectsEnabled || showAddToQuote) && (
                <div className="sui-grid sui-grid-cols-2 sui-gap-4">
                  {showFavorites && addToListNode}
                  {showAddToQuote && addToQuoteNode}
                  {isAddToProjectsEnabled && addToProjectsNode}
                </div>
              )}
              {showPaypalMessaging && (
                <div className="sui-w-full">
                  <PaypalPriceMessaging
                    payInFour
                    orderTotal={isGenericMessaging ? null : pricing?.value}
                    fromPage="pip"
                    fontSize="14"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

MajorApplianceBuyBox.displayName = 'MajorApplianceBuyBox';

MajorApplianceBuyBox.themeProps = {
  hidePaypal: bool,
  hideAddToQuoteButton: bool
};

MajorApplianceBuyBox.defaultThemeProps = {
  hidePaypal: false,
  hideAddToQuoteButton: false
};

MajorApplianceBuyBox.propTypes = {
  /**
   * The number of clicks you are allowed before seeing a warning.
   */
  itemId: string,
  stash: shape({
    pageType: string,
    sharedSection: string,
    section: string,
    component: string
  }),
  showCards: bool,
  sticky: bool,
  showFavorites: bool,
  showAddToProject: bool,
  noPipSpacing: bool
};

MajorApplianceBuyBox.defaultProps = {
  showFavorites: false,
  itemId: null,
  stash: {},
  sticky: false,
  showCards: false,
  showAddToProject: false,
  noPipSpacing: false
};

export const majorApplianceDataModel = {
  clientOnlyProduct: alias('product').params({
    itemId: stringType().isRequired()
  }).shape({
    identifiers: shapeType({
      productLabel: stringType()
    }),
    fulfillment: client(params({ storeId: stringType(), zipCode: stringType() }).shape({
      fulfillmentOptions: arrayOfType(shapeType({
        type: stringType(),
        fulfillable: boolType(),
        services: arrayOfType(shapeType({
          locations: arrayOfType(shapeType({
            inventory: shapeType({
              quantity: numberType()
            }),
            isAnchor: boolType(),
            locationId: stringType(),
            storeName: stringType(),
            type: stringType()
          })),
          type: stringType()
        }))
      }))
    }))
  })
};

MajorApplianceBuyBox.dataModel = extend(
  majorApplianceDataModel,
  MajorApplianceFulfillment,
  BuyboxAddToList,
  BuyboxAddToProject,
  AddToQuote,
  PartsAndServicesDropDownContainer
);

const ErrorMABuybox = withErrorBoundary(MajorApplianceBuyBox);

export { ErrorMABuybox as MajorApplianceBuyBox };
